import { getRolesUserAndSwitchUser } from '@/lib/auth/getRolesUserAndSwitchUser'
import { getEntityForRoute } from '@/lib/hook/useEntity'
import { routeNotAuthenticate } from '@/lib/routes/CONSTANT_ROUTES'
import getListRouteAvailable, {
  getListRouteAvailableWithoutEntity,
} from '@/lib/routes/getAllRoutes'

type Args = {
  route: string
  values?: any
  args?: string
  forceEntity?: string
}

type Output = {
  href: string
  as: string
  pathname: string
  query: Record<string, any>
}

const getRoutes = async ({ route, values = {}, args = '', forceEntity }: Args): Promise<Output> => {
  const { isAuthentificated, loggedUserRoles } = getRolesUserAndSwitchUser()
  // `forceEntity` : resoudre le nom DANS une entite donnee plutot que dans l'entite courante.
  // Indispensable pour les noms partages (« Dashboard », « campaigns »...) : une action PLV
  // servie par /me/actions doit mener au tableau de bord PLV, meme lue depuis l'espace radio.
  // Le parametre etait declare dans Args mais jamais lu — la cible tombait sur l'entite
  // affichee au moment du clic.
  const entity = forceEntity ?? (await getEntityForRoute())

  const routeSelectByRole = !!isAuthentificated
    ? getListRouteAvailable(loggedUserRoles, entity)
    : [...routeNotAuthenticate]

  // L'entite courante reste PRIORITAIRE : plusieurs entites declarent des routes de meme
  // nom (« Dashboard », « Parametrage »...), et c'est elle qui designe la bonne.
  // REPLI sur le catalogue complet (roles seuls) quand le nom n'y existe pas : naviguer ne
  // bascule plus l'entite (elle ne sert qu'a classer le menu), une cible d'un autre module
  // doit donc rester atteignable au lieu de retomber sur '/'.
  const routeFind =
    routeSelectByRole.find(oneRoute => oneRoute.route === route) ??
    (isAuthentificated
      ? getListRouteAvailableWithoutEntity(loggedUserRoles).find(
          (oneRoute: any) => oneRoute.route === route
        )
      : undefined)

  const routeSelect = routeFind?.href

  const urlLocalised = !!routeSelect ? `${routeSelect}${args}` : '/'

  let as = urlLocalised
  Object.keys(values).forEach(key => {
    // Convention : la clé est le nom du placeholder sans crochets (ex: 'dossierId' substitue '[dossierId]')
    as = as.replace(new RegExp(`\\[${key}\\]`, 'g'), values[key])
    // Rétrocompat : on remplace aussi la clé brute (au cas où un appelant aurait passé '[dossierId]')
    as = as.replace(key, values[key])
  })

  if (routeFind?.queryParams) {
    Object.keys(routeFind.queryParams).forEach(key => {
      as = as.replace(new RegExp(`\\[${key}\\]`, 'g'), routeFind.queryParams[key])
      as = as.replace(key, routeFind.queryParams[key])
    })
  }

  // `args` porte une QUERYSTRING brute (ex: '?codeUgaEmpty=1'). `pushRoute` navigue via
  // `Router.push({ pathname, query })` : tout ce qui reste dans `pathname` est encode, donc un
  // '?' laisse la devenait '%3F' -> URL du type `/industryoffice/group/pharmacies%3FcodeUgaEmpty=1`
  // et 404. On separe donc la querystring du chemin et on la fusionne dans `query`, la seule
  // place ou Next la restitue telle quelle.
  const [pathname, queryString = ''] = urlLocalised.split('?')
  const argsQuery = queryString
    ? Object.fromEntries(new URLSearchParams(queryString).entries())
    : {}

  return {
    as,
    href: urlLocalised,
    pathname,
    query: { ...values, ...routeFind?.queryParams, ...argsQuery },
  }
}

export default getRoutes
